import { useState, useEffect, useRef } from 'react';

const useCounterAnimation = (endValue, duration = 2000, startDelay = 0) => {
  const [currentValue, setCurrentValue] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    const timer = setTimeout(() => {
      let startTime = null;
      const startValue = 0;
      
      // Parse the end value properly handling M, K suffixes
      let numericEndValue;
      const endStr = endValue.toString();
      
      if (endStr.includes('M')) {
        numericEndValue = parseFloat(endStr.replace(/[M+,]/g, '')) * 1000000;
      } else if (endStr.includes('K')) {
        numericEndValue = parseFloat(endStr.replace(/[K+,]/g, '')) * 1000;
      } else {
        numericEndValue = parseInt(endStr.replace(/[,+%]/g, ''));
      }

      const animate = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        
        // Easing function for smooth animation
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        const current = Math.floor(startValue + (numericEndValue * easeOutQuart));
        
        setCurrentValue(current);
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    }, startDelay);

    return () => clearTimeout(timer);
  }, [isVisible, endValue, duration, startDelay]);

  // Format the current value back to display format with consistent width
  const formatValue = (value) => {
    const endStr = endValue.toString();
    
    if (endStr.includes('M+')) {
      const formatted = (value / 1000000).toFixed(1);
      return (formatted.endsWith('.0') ? formatted.slice(0, -2) : formatted) + 'M+';
    } else if (endStr.includes('M')) {
      const formatted = (value / 1000000).toFixed(1);
      return (formatted.endsWith('.0') ? formatted.slice(0, -2) : formatted) + 'M';
    } else if (endStr.includes('K')) {
      return (value / 1000).toFixed(0) + 'K';
    } else if (endStr.includes('%')) {
      return value + '%';
    } else if (endStr.includes('+')) {
      return value.toLocaleString() + '+';
    }
    return value.toLocaleString();
  };

  return [formatValue(currentValue), elementRef];
};

export default useCounterAnimation;